import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import { DrugCostInfo, DrugInfo, PharmacyType, Plan } from "../../@types";
import { AppStore } from "../../app/store";
import { SuppressionHandler } from "../../components";
import {
  $$,
  isMedicareAdvantageType,
  isPdPlanType,
  useTranslate,
} from "../../helpers";
import {
  getInNetworkPrimaryDoctorCost,
  getHealthDeductible,
  formatCost,
} from "../../helpers/benefitHelpers";

export const whatYoullPayFeatureTestId = "what-youll-pay-feature";
export const whatYoullPayFeatureValueTestId = "what-youll-pay-feature__value";

export const WhatYoullPay: React.FunctionComponent<{
  plan: Plan;
  drugCostInfo: DrugCostInfo;
  drugInfo: DrugInfo[];
}> = ({ plan, drugCostInfo, drugInfo }) => {
  const {
    state: { prescriptions, pharmacyType },
  } = useContext(AppStore.AppContext);
  const [numDrugsCovered, setNumDrugsCovered] = useState<number | undefined>(
    undefined
  );
  const t = useTranslate();
  const { plan_type, contract_year } = plan;
  let features: {
    label: string | JSX.Element;
    value: number | string | JSX.Element;
  }[] = [];

  const FeatureSubtitle: FunctionComponent = ({ children }) => (
    <div className="ds-u-md-margin-top--1 ds-u-font-size--sm ds-u-font-weight--normal">
      {children}
    </div>
  );

  useEffect(() => {
    const covered =
      drugInfo?.reduce((prev, curr) => prev + (curr.on_formulary ? 1 : 0), 0) ??
      0;

    setNumDrugsCovered(covered);
  }, [drugInfo]);

  features.push({
    label: t("what_youll_pay.total_monthly_premium"),
    value: (
      <SuppressionHandler
        redactions={plan.redactions}
        checkShouldSuppressDrugPricing
      >
        {formatCost(plan.calculated_monthly_premium)}
      </SuppressionHandler>
    ),
  });

  if (isMedicareAdvantageType(plan_type)) {
    const primaryDoctor = getInNetworkPrimaryDoctorCost(plan, t);
    const healthDeductible = getHealthDeductible(plan);

    features = [
      ...features,
      {
        label: t("what_youll_pay.health_deductible"),
        value: (
          <SuppressionHandler redactions={plan.redactions}>
            <span>{healthDeductible.cost}</span>
            {healthDeductible.label && (
              <FeatureSubtitle>{healthDeductible.label}</FeatureSubtitle>
            )}
          </SuppressionHandler>
        ),
      },
      {
        label: t("what_youll_pay.primary_doctor"),
        value: (
          <SuppressionHandler redactions={plan.redactions}>
            <span>{primaryDoctor.cost}</span>
            {primaryDoctor.label && (
              <FeatureSubtitle>{primaryDoctor.label}</FeatureSubtitle>
            )}
          </SuppressionHandler>
        ),
      },
    ];
  }

  if (prescriptions.length) {
    const { lowest_mail_total, lowest_retail_total } = drugCostInfo;
    const lowestMailNum = Number(lowest_mail_total);
    const lowestRetailNum = Number(lowest_retail_total);

    const drugsCovered = (
      <FormattedHTMLMessage
        id="what_youll_pay.num_covered"
        values={{
          numDrugsCovered: numDrugsCovered,
          totalDrugs: prescriptions.length,
        }}
      />
    );

    const formattedLowestMail = isNaN(lowestMailNum)
      ? lowest_mail_total
      : $$(lowestMailNum);
    const formattedLowestRetail = isNaN(lowestRetailNum)
      ? lowest_retail_total
      : $$(lowestRetailNum);

    const hasRetail = [
      PharmacyType.RETAIL,
      PharmacyType.MAIL_AND_RETAIL,
    ].includes(pharmacyType);
    const hasMail = [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
      pharmacyType
    );

    if (isMedicareAdvantageType(plan_type)) {
      const isMailOnly = pharmacyType === PharmacyType.MAIL;

      features = [
        ...features,
        {
          label:
            hasRetail && hasMail ? (
              <FormattedMessage
                id="what_youll_pay.lowest_cost_retail_pharmacy"
                values={{ year: contract_year }}
              />
            ) : (
              <FormattedMessage
                id="what_youll_pay.lowest_cost_pharmacy"
                values={{ year: contract_year }}
              />
            ),
          value: (
            <SuppressionHandler
              redactions={plan.redactions}
              suppressOnlyDrugPricing
            >
              <div>
                {isMailOnly ? formattedLowestMail : formattedLowestRetail}
              </div>
              <FeatureSubtitle>{drugsCovered}</FeatureSubtitle>
            </SuppressionHandler>
          ),
        },
      ];
    }

    if (isPdPlanType(plan_type)) {
      if (hasRetail) {
        features = [
          ...features,
          {
            label: (
              <FormattedMessage
                id="what_youll_pay.retail_pharmacy"
                values={{ year: contract_year }}
              />
            ),
            value: (
              <SuppressionHandler
                redactions={plan.redactions}
                suppressOnlyDrugPricing
              >
                <div>{formattedLowestRetail}</div>
                <FeatureSubtitle>{drugsCovered}</FeatureSubtitle>
              </SuppressionHandler>
            ),
          },
        ];
      }

      if (hasMail) {
        features = [
          ...features,
          {
            label: (
              <FormattedMessage
                id="what_youll_pay.mail_pharmacy"
                values={{ year: contract_year }}
              />
            ),
            value: (
              <SuppressionHandler
                redactions={plan.redactions}
                suppressOnlyDrugPricing
              >
                <div>{formattedLowestMail}</div>
                <FeatureSubtitle>{drugsCovered}</FeatureSubtitle>
              </SuppressionHandler>
            ),
          },
        ];
      }
    }
  }

  return (
    <div className="WhatYoullPay">
      <div className="WhatYoullPay__contents">
        <div className="WhatYoullPay__titleContainer">
          <h3 className="ds-text-heading--2xl ds-u-margin--0">
            {t("what_youll_pay.title")}
          </h3>
        </div>

        <dl className="WhatYoullPay__features">
          {features.map((feature, fIndex) => (
            <div
              key={fIndex}
              className="WhatYoullPay__feature"
              data-testid={`${whatYoullPayFeatureTestId}-${fIndex}`}
            >
              <dt className="WhatYoullPay__feature-label">{feature.label}</dt>
              <dd
                className="WhatYoullPay__feature-value"
                data-testid={`${whatYoullPayFeatureValueTestId}-${fIndex}`}
              >
                {feature.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};
